<script setup lang="ts">
/**
 * @file Text-only, box-less "button". Style this as you would normal text.
 */
import OzBaseButton, { OzBaseButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'

const {
  darkMode = 'auto',
  disabled = false,
  href,
  hrefMode = OzBaseButtonHrefMode.Default,
  colorScheme = OzTextButtonColorScheme.Primary,
  text,
  title,
} = defineProps<{
  darkMode?: boolean | 'auto'
  disabled?: boolean
  href?: string
  hrefMode?: OzBaseButtonHrefMode
  colorScheme?: OzTextButtonColorScheme | null
  text?: string
  title?: string
}>()

defineEmits<{
  (name: 'mousedown', event: MouseEvent): void
  (name: 'keydown', event: KeyboardEvent): void
  (name: 'click', event: MouseEvent): void
  (name: 'focus', event: FocusEvent): void
  (name: 'blur', event: FocusEvent): void
}>()
</script>

<script lang="ts">
export enum OzTextButtonColorScheme {
  Bare = 'Bare',
  Primary = 'Primary',
  Secondary = 'Secondary',
  SecondaryMuted = 'SecondaryMuted',
  Danger = 'Danger',
  Active = 'Active',
}

export { OzBaseButtonHrefMode as OzTextButtonHrefMode } from '@@/library/v4/components/OzBaseButton.vue'
export default {}
</script>

<template>
  <OzBaseButton
    :disabled="disabled"
    :href="href"
    :href-mode="hrefMode"
    :title="title || text"
    :class="[
      // Resets <button> background.
      // Invariant: OzTextButton doesn't have a background.
      // If you need a background, use OzContainedButton.
      'bg-transparent',

      // Color schemes
      colorScheme === OzTextButtonColorScheme.Primary && [
        // Base
        'text-padlet-pink',
        // Hovered
        'hover-hover:hover:text-padlet-pink-800',
        // Active
        'hover-hover:active:text-padlet-pink-800',
        // Focused
        'focus-visible:text-padlet-pink-800',
        // Disabled - Base
        {
          'aria-disabled:text-padlet-pink-100': darkMode === false,
          'aria-disabled:text-padlet-pink-900': darkMode === true,
          'aria-disabled:text-padlet-pink-100 dark:aria-disabled:text-padlet-pink-900': darkMode === 'auto',
        },
        // Disabled - Hover
        {
          'hover-hover:aria-disabled:text-padlet-pink-100': darkMode === false,
          'hover-hover:aria-disabled:text-padlet-pink-900': darkMode === true,
          'hover-hover:aria-disabled:text-padlet-pink-100 hover-hover:dark:aria-disabled:text-padlet-pink-900':
            darkMode === 'auto',
        },
      ],
      colorScheme !== null &&
        [OzTextButtonColorScheme.Secondary, OzTextButtonColorScheme.SecondaryMuted].includes(colorScheme) && [
          // Base
          {
            'text-dark-text-200': darkMode === false,
            'text-light-text-200': darkMode === true,
            'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
          },
          // Hovered
          colorScheme === OzTextButtonColorScheme.Secondary && {
            'hover-hover:hover:text-grape-500': darkMode === false,
            'hover-hover:hover:text-canary-500': darkMode === true,
            'hover-hover:hover:text-grape-500 hover-hover:dark:hover:text-canary-500': darkMode === 'auto',
          },
          colorScheme === OzTextButtonColorScheme.SecondaryMuted && {
            'hover-hover:hover:text-dark-text-100': darkMode === false,
            'hover-hover:hover:text-light-text-100': darkMode === true,
            'hover-hover:hover:text-dark-text-100 hover-hover:dark:hover:text-light-text-100': darkMode === 'auto',
          },
          // Active
          {
            'hover-hover:active:text-grape-500': darkMode === false,
            'hover-hover:active:text-canary-500': darkMode === true,
            'hover-hover:active:text-grape-500 hover-hover:dark:active:text-canary-500': darkMode === 'auto',
          },
          // Focused
          {
            'focus-visible:text-grape-500': darkMode === false,
            'focus-visible:text-canary-500': darkMode === true,
            'focus-visible:text-grape-500 dark:focus-visible:text-canary-500': darkMode === 'auto',
          },
          // Disabled - Base
          {
            'aria-disabled:text-dark-text-400': darkMode === false,
            'aria-disabled:text-light-text-400': darkMode === true,
            'aria-disabled:text-dark-text-400 dark:aria-disabled:text-light-text-400': darkMode === 'auto',
          },
          // Disabled - Hover
          {
            'hover-hover:aria-disabled:text-dark-text-400': darkMode === false,
            'hover-hover:aria-disabled:text-light-text-400': darkMode === true,
            'hover-hover:aria-disabled:text-dark-text-400 hover-hover:dark:aria-disabled:text-light-text-400 ':
              darkMode === 'auto',
          },
        ],
      colorScheme === OzTextButtonColorScheme.Danger && [
        // Base
        'text-danger-100',
        // Hovered
        'hover-hover:hover:text-scarlet-600',
        // Active
        'hover-hover:active:text-scarlet-600',
        // Focused
        'focus-visible:text-scarlet-600',
        // Disabled - Base
        'aria-disabled:text-scarlet-700',
        // Disabled - Hover
        'hover-hover:aria-disabled:text-scarlet-700',
      ],
      colorScheme === OzTextButtonColorScheme.Active && [
        // Base
        {
          'text-grape-500': darkMode === false,
          'text-canary-500': darkMode === true,
          'text-grape-500 dark:text-canary-500': darkMode === 'auto',
        },
      ],
    ]"
    @mousedown="$emit('mousedown', $event)"
    @keydown="$emit('keydown', $event)"
    @click="$emit('click', $event)"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
  >
    {{ text }}
    <slot></slot>
  </OzBaseButton>
</template>
